import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WalletReadyState } from '@solana/wallet-adapter-base'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal, WalletMultiButton } from '@solana/wallet-adapter-react-ui'
import { PublicKey } from '@solana/web3.js'
import {
  ENABLE_SDK_CREATE,
  PUMP_CREATE_URL,
  TOKEN,
} from '../lib/constants'
import { createTokenOnPump } from '../lib/createCoin'
import {
  applyWalletFeeShares,
  normalizeTwitterHandle,
  percentToRecipientBps,
} from '../lib/feeShare'
import { lookupFeeLink, registerLaunch } from '../lib/launchesApi'
import { uploadTokenMetadata } from '../lib/uploadMetadata'

/** Copy with clipboard API + textarea/execCommand fallback for mobile / non-secure contexts. */
async function copyText(value: string, textarea?: HTMLTextAreaElement | null): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    /* fall through */
  }

  try {
    const el = textarea ?? document.createElement('textarea')
    const ephemeral = !textarea
    if (ephemeral) {
      el.value = value
      el.setAttribute('readonly', '')
      el.style.position = 'fixed'
      el.style.left = '-9999px'
      document.body.appendChild(el)
    }
    el.focus()
    el.select()
    el.setSelectionRange(0, value.length)
    const ok = document.execCommand('copy')
    if (ephemeral) document.body.removeChild(el)
    return ok
  } catch {
    return false
  }
}

function CopyField({
  label,
  value,
  note,
}: {
  label: string
  value: string
  note?: string
}) {
  const [copied, setCopied] = useState(false)
  const [hint, setHint] = useState(false)
  const taRef = useRef<HTMLTextAreaElement>(null)

  const onCopy = async () => {
    const ok = await copyText(value, taRef.current)
    if (ok) {
      setCopied(true)
      setHint(false)
      setTimeout(() => setCopied(false), 1500)
    } else {
      taRef.current?.focus()
      taRef.current?.select()
      setHint(true)
      setTimeout(() => setHint(false), 4000)
    }
  }

  return (
    <div className="copy-field">
      <label>{label}</label>
      <div className="copy-row">
        <textarea
          ref={taRef}
          readOnly
          value={value}
          rows={value.length > 80 ? 3 : 1}
          onFocus={(e) => e.currentTarget.select()}
        />
        <button type="button" className="btn ghost small" onClick={() => void onCopy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {note && <p className="field-note">{note}</p>}
      {hint && (
        <p className="copy-hint">
          Clipboard blocked — text is selected; press Ctrl/Cmd+C (or long-press → Copy).
        </p>
      )}
    </div>
  )
}

function FormField({
  label,
  value,
  onChange,
  error,
  as = 'input',
  rows = 3,
  maxLength,
  placeholder,
  required,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  error?: string | null
  as?: 'input' | 'textarea'
  rows?: number
  maxLength?: number
  placeholder?: string
  required?: boolean
}) {
  const id = `field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <div className={`form-field${error ? ' has-error' : ''}`}>
      <label htmlFor={id}>
        {label}
        {required ? <span className="req"> *</span> : null}
      </label>
      {as === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      )}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

/** Map raw wallet / RPC errors into clear user-facing messages. */
function friendlyCreateError(raw: string): string {
  const lower = raw.toLowerCase()

  if (
    lower.includes('connect a solana wallet') ||
    lower.includes('wallet not connected') ||
    (lower.includes('publickey') && lower.includes('null'))
  ) {
    return 'Connect Phantom or Solflare first, then try Launch again.'
  }

  if (
    lower.includes('user rejected') ||
    lower.includes('user denied') ||
    lower.includes('rejected the request') ||
    lower.includes('cancelled') ||
    lower.includes('canceled') ||
    lower.includes('approval denied')
  ) {
    return 'You rejected the transaction in your wallet. No funds were spent. Click Launch again when ready.'
  }

  if (
    lower.includes('insufficient') ||
    lower.includes('0x1') ||
    lower.includes('attempt to debit an account but found no record of a prior credit') ||
    lower.includes('insufficient funds') ||
    lower.includes('insufficient lamports')
  ) {
    return 'Insufficient SOL for rent + network fees. Fund your wallet with a little SOL on mainnet-beta and retry.'
  }

  if (
    lower.includes('403') ||
    lower.includes('forbidden') ||
    lower.includes('429') ||
    lower.includes('too many requests')
  ) {
    return "RPC blocked — we're using a public endpoint; try again"
  }

  if (
    lower.includes('failed to fetch') ||
    lower.includes('network error') ||
    lower.includes('503') ||
    lower.includes('timeout') ||
    lower.includes('blockhash') ||
    lower.includes('rpc') ||
    lower.includes('fetch error') ||
    lower.includes('connection')
  ) {
    return `RPC or network error: ${raw}. Try again, or set VITE_SOLANA_RPC to a reliable mainnet endpoint and rebuild.`
  }

  if (lower.includes('cannot sign') || lower.includes('no send/sign')) {
    return 'Connected wallet cannot sign transactions. Use Phantom or Solflare and ensure it is unlocked.'
  }

  if (
    lower.includes('metadata upload') ||
    lower.includes('ipfs-upload') ||
    lower.includes('pinata') ||
    lower.includes('vite_pinata')
  ) {
    return `Metadata upload failed before the wallet prompt: ${raw}`
  }

  return raw
}

type FormState = {
  name: string
  symbol: string
  description: string
  website: string
  twitter: string
  telegram: string
}

type FormErrors = Partial<Record<keyof FormState, string>>

function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {}
  const name = form.name.trim()
  const symbol = form.symbol.trim()
  const description = form.description.trim()
  const website = form.website.trim()

  if (!name) errors.name = 'Name is required.'
  else if (name.length > 32) errors.name = 'Name must be 32 characters or fewer.'

  if (!symbol) errors.symbol = 'Symbol is required.'
  else if (!/^[A-Za-z0-9]+$/.test(symbol)) {
    errors.symbol = 'Symbol must be letters/numbers only (no spaces).'
  } else if (symbol.length < 1 || symbol.length > 10) {
    errors.symbol = 'Symbol must be 1–10 characters.'
  }

  if (!description) errors.description = 'Description is required.'
  else if (description.length > 500) {
    errors.description = 'Description must be 500 characters or fewer.'
  }

  if (website) {
    try {
      const u = new URL(website)
      if (u.protocol !== 'http:' && u.protocol !== 'https:') {
        errors.website = 'Website must start with http:// or https://'
      }
    } catch {
      errors.website = 'Enter a valid URL (e.g. https://testlaunch.site).'
    }
  }

  return errors
}

export function LaunchPanel({ onBack }: { onBack?: () => void }) {
  const { connection } = useConnection()
  const wallet = useWallet()
  const { setVisible } = useWalletModal()
  const [busy, setBusy] = useState(false)
  const [busyPhase, setBusyPhase] = useState<'upload' | 'create' | 'fees' | 'register' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [walletError, setWalletError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({})
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [uploadedUri, setUploadedUri] = useState<string | null>(null)
  const [uploadedImageUri, setUploadedImageUri] = useState<string | null>(null)
  const [devBuy, setDevBuy] = useState('')
  const [feeHandle, setFeeHandle] = useState('')
  const [feePercent, setFeePercent] = useState('10')
  const [feeShareNote, setFeeShareNote] = useState<string | null>(null)
  const [result, setResult] = useState<{
    mint: string
    signature: string
    pumpUrl: string
  } | null>(null)

  const [form, setForm] = useState<FormState>({
    name: TOKEN.name,
    symbol: TOKEN.symbol,
    description: TOKEN.description,
    website: TOKEN.website,
    twitter: '',
    telegram: '',
  })

  const setField = useCallback((key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const displayName = form.name.trim() || 'Your token'
  const displaySymbol = form.symbol.trim() || 'TICKER'
  const launchLabel = `Launch $${displaySymbol}`

  const onLogoChange = useCallback((file: File | null) => {
    setLogoFile(file)
    setLogoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return file ? URL.createObjectURL(file) : null
    })
  }, [])

  const onCreate = useCallback(async () => {
    setError(null)
    setResult(null)
    setUploadedUri(null)
    setUploadedImageUri(null)
    setFeeShareNote(null)

    const errors = validateForm(form)
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) {
      setError('Fix the highlighted fields, then try Launch again.')
      return
    }

    const handleNorm = feeHandle.trim()
      ? normalizeTwitterHandle(feeHandle)
      : null
    if (feeHandle.trim() && !handleNorm) {
      setError('Fee-share X handle looks invalid (use @user, 1–15 chars).')
      return
    }
    const pct = feeHandle.trim() ? Number(feePercent) : 0
    if (feeHandle.trim() && (Number.isNaN(pct) || pct < 0 || pct > 50)) {
      setError('Fee-share percent must be between 0 and 50.')
      return
    }
    const recipientBps = handleNorm ? percentToRecipientBps(pct) : 0

    setBusy(true)
    try {
      setBusyPhase('upload')
      const uploaded = await uploadTokenMetadata({
        name: form.name.trim(),
        symbol: form.symbol.trim(),
        description: form.description.trim(),
        website: form.website.trim() || undefined,
        twitter: form.twitter.trim() || undefined,
        telegram: form.telegram.trim() || undefined,
        showName: true,
        imageFile: logoFile,
      })
      setUploadedUri(uploaded.metadataUri)
      setUploadedImageUri(uploaded.imageUri ?? null)

      setBusyPhase('create')
      const res = await createTokenOnPump(connection, wallet, {
        name: form.name.trim(),
        symbol: form.symbol.trim(),
        description: form.description.trim(),
        website: form.website.trim() || undefined,
        uri: uploaded.metadataUri,
      })
      setResult(res)

      let feeShareStatus: 'none' | 'on_chain' | 'pending_link' = 'none'
      let feeShareWallet: string | null = null

      if (handleNorm && recipientBps > 0) {
        const link = await lookupFeeLink(handleNorm)
        if (link?.wallet) {
          try {
            setBusyPhase('fees')
            await applyWalletFeeShares(
              connection,
              wallet,
              new PublicKey(res.mint),
              {
                recipientWallet: new PublicKey(link.wallet),
                recipientBps,
              },
            )
            feeShareStatus = 'on_chain'
            feeShareWallet = link.wallet
            setFeeShareNote(
              `On-chain fee share: ${pct}% of creator fees → @${handleNorm} (${link.wallet.slice(0, 4)}…${link.wallet.slice(-4)}).`,
            )
          } catch (feeErr) {
            feeShareStatus = 'pending_link'
            setFeeShareNote(
              `Token launched, but fee-share tx failed: ${
                feeErr instanceof Error ? feeErr.message : String(feeErr)
              }. Recorded as pending for @${handleNorm}.`,
            )
          }
        } else {
          feeShareStatus = 'pending_link'
          setFeeShareNote(
            `@${handleNorm} has not linked a Solana wallet yet. 100% creator fees for now; share recorded as pending. (Platform.X social PDAs are not used — fees would be at risk.)`,
          )
        }
      }

      try {
        setBusyPhase('register')
        await registerLaunch({
          mint: res.mint,
          name: form.name.trim(),
          symbol: form.symbol.trim(),
          description: form.description.trim(),
          imageUri: uploaded.imageUri || TOKEN.imageUri,
          website: form.website.trim() || TOKEN.website,
          creatorWallet: wallet.publicKey!.toBase58(),
          twitterFeeHandle: handleNorm,
          twitterFeeBps: recipientBps || null,
          feeShareStatus,
          feeShareWallet,
          createdAt: new Date().toISOString(),
          signature: res.signature,
          pumpUrl: res.pumpUrl,
        })
      } catch (regErr) {
        console.warn('[exfund] registerLaunch failed', regErr)
        setFeeShareNote(
          (prev) =>
            (prev ? prev + ' ' : '') +
            'Launch registry write failed — token still exists on-chain.',
        )
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setError(friendlyCreateError(msg))
    } finally {
      setBusy(false)
      setBusyPhase(null)
    }
  }, [connection, wallet, form, logoFile, feeHandle, feePercent])

  const ctaLabel = useMemo(() => {
    if (!wallet.connected) return 'Connect wallet'
    if (busy) {
      if (busyPhase === 'upload') return 'Uploading metadata…'
      if (busyPhase === 'fees') return 'Setting fee share… approve in wallet'
      if (busyPhase === 'register') return 'Saving to Explore…'
      return 'Creating… approve in wallet'
    }
    return launchLabel
  }, [wallet.connected, busy, busyPhase, launchLabel])

  /** True when at least one adapter is usable (Standard-installed or loadable). */
  const hasUsableWallet = useMemo(
    () =>
      wallet.wallets.some(
        (w) =>
          w.readyState === WalletReadyState.Installed ||
          w.readyState === WalletReadyState.Loadable,
      ),
    [wallet.wallets],
  )

  // Delay the "install wallet" hint so Wallet Standard can register extensions.
  const [showInstallHint, setShowInstallHint] = useState(false)
  useEffect(() => {
    if (hasUsableWallet) {
      setShowInstallHint(false)
      return
    }
    const t = window.setTimeout(() => setShowInstallHint(true), 600)
    return () => window.clearTimeout(t)
  }, [hasUsableWallet])

  // Clear connect errors once linked. If a wallet is selected but still
  // disconnected after connecting finishes, surface a retryable message.
  const prevConnecting = useRef(false)
  useEffect(() => {
    if (wallet.connected) {
      setWalletError(null)
      prevConnecting.current = false
      return
    }
    const wasConnecting = prevConnecting.current
    prevConnecting.current = wallet.connecting
    if (wasConnecting && !wallet.connecting && wallet.wallet && !wallet.connected) {
      setWalletError(
        `Could not connect to ${wallet.wallet.adapter.name}. Approve the extension prompt, or pick another wallet.`,
      )
    }
  }, [wallet.connected, wallet.connecting, wallet.wallet])

  const retryWalletConnect = useCallback(async () => {
    setWalletError(null)
    if (!wallet.wallet) {
      setVisible(true)
      return
    }
    try {
      await wallet.connect()
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e)
      const lower = raw.toLowerCase()
      if (
        lower.includes('user rejected') ||
        lower.includes('user denied') ||
        lower.includes('rejected the request') ||
        lower.includes('cancelled') ||
        lower.includes('canceled')
      ) {
        setWalletError('Connection rejected in the wallet extension. Click Retry and approve the request.')
      } else if (lower.includes('not ready') || lower.includes('walletnotready')) {
        setWalletError('Wallet extension not ready. Unlock it (or install Phantom/Solflare) and try again.')
      } else {
        setWalletError(raw || 'Wallet connect failed.')
      }
    }
  }, [wallet, setVisible])

  const onPrimaryCta = () => {
    if (!wallet.connected) {
      setVisible(true)
      return
    }
    void onCreate()
  }

  return (
    <div className="launch-panel">
      <div className="create-top">
        {onBack ? (
          <button type="button" className="back-pill" onClick={onBack}>
            ← Back
          </button>
        ) : (
          <span />
        )}
        <span className="muted" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
          Solana · pump.fun backend
        </span>
      </div>

      <div className="create-shell">
        <div className="create-form-col">
          <h1>Launch token</h1>

          {!ENABLE_SDK_CREATE && (
            <p className="alert warn">
              On-site create is disabled (
              <code>VITE_ENABLE_SDK_CREATE=false</code>). Rebuild with the flag
              set to <code>true</code> (default) to enable Launch.
            </p>
          )}

          <div className="form-row-2">
            <FormField
              label="Name"
              value={form.name}
              onChange={(v) => setField('name', v)}
              error={fieldErrors.name}
              maxLength={32}
              placeholder="Token name"
              required
            />
            <FormField
              label="Ticker"
              value={form.symbol}
              onChange={(v) => setField('symbol', v.toUpperCase())}
              error={fieldErrors.symbol}
              maxLength={10}
              placeholder="symbol"
              required
            />
          </div>

          <FormField
            label="Description"
            value={form.description}
            onChange={(v) => setField('description', v)}
            error={fieldErrors.description}
            as="textarea"
            rows={4}
            maxLength={500}
            placeholder="What is this token about?"
            required
          />

          <div className="upload-box">
            <span className="field-label">Token image</span>
            <div className={`upload-drop${logoPreview || logoFile ? ' has-file' : ''}`}>
              {logoPreview || TOKEN.logoPath ? (
                <img
                  className="thumb"
                  src={logoPreview || TOKEN.logoPath}
                  alt="Token logo preview"
                  width={88}
                  height={88}
                />
              ) : (
                <div className="upload-icon">⬆</div>
              )}
              <p className="upload-hint">
                {logoFile ? logoFile.name : 'PNG, JPG, WEBP or GIF · max 5 MB'}
              </p>
              <div className="upload-actions">
                <label className="btn ghost small logo-file-label">
                  Choose image
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null
                      if (f && f.size > 5_000_000) {
                        setError('Logo must be 5 MB or smaller.')
                        e.target.value = ''
                        return
                      }
                      onLogoChange(f)
                    }}
                  />
                </label>
                {logoFile && (
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => onLogoChange(null)}
                  >
                    Use default
                  </button>
                )}
              </div>
            </div>
          </div>

          <details className="optional-block">
            <summary>Socials (optional)</summary>
            <div className="optional-body">
              <FormField
                label="Website"
                value={form.website}
                onChange={(v) => setField('website', v)}
                error={fieldErrors.website}
                maxLength={200}
                placeholder="https://"
              />
              <FormField
                label="X profile"
                value={form.twitter}
                onChange={(v) => setField('twitter', v)}
                maxLength={120}
                placeholder="https://x.com/…"
              />
              <FormField
                label="Telegram"
                value={form.telegram}
                onChange={(v) => setField('telegram', v)}
                maxLength={120}
                placeholder="https://t.me/…"
              />
            </div>
          </details>

          <details className="optional-block" open={Boolean(feeHandle.trim())}>
            <summary>Fee share to X (optional)</summary>
            <div className="optional-body">
              <p className="field-note">
                Bags-like: allocate a % of <strong>creator fees</strong> to an
                X handle. Shareholders are Solana wallets only (not Platform.X
                PDAs). If the handle already linked a wallet on the Fees
                tab, we set the split on-chain after create; otherwise the share
                stays pending until they link.
              </p>
              <FormField
                label="X handle"
                value={feeHandle}
                onChange={setFeeHandle}
                maxLength={32}
                placeholder="@friend"
              />
              <FormField
                label="Share of creator fees (%)"
                value={feePercent}
                onChange={setFeePercent}
                placeholder="10"
              />
              <p className="field-note">
                0–50%. Remainder stays with your creator wallet. Total on-chain
                shares always equal 100%.
              </p>
            </div>
          </details>

          <details className="optional-block">
            <summary>Developer buy (optional)</summary>
            <div className="optional-body">
              <FormField
                label="Buy amount (SOL)"
                value={devBuy}
                onChange={setDevBuy}
                placeholder="0"
              />
              <p className="field-note">
                UI only for now — this launch path creates the token without an
                initial developer buy. Fund and buy on the bonding curve after
                mint if you want an early position.
              </p>
            </div>
          </details>

          {error && (
            <div className="alert error" role="alert">
              <strong>Launch failed:</strong> {error}
            </div>
          )}

          {result && (
            <div className="alert success" role="status">
              <p>
                <strong>Launched ${displaySymbol}.</strong> Mint:{' '}
                <code className="mint-code">{result.mint}</code>
              </p>
              <p>
                Transaction:{' '}
                <a
                  href={`https://solscan.io/tx/${result.signature}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Solscan
                </a>{' '}
                (<code>{result.signature.slice(0, 16)}…</code>)
              </p>
              <p className="success-actions">
                <a
                  className="btn primary"
                  href={`https://solscan.io/token/${result.mint}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Mint on Solscan
                </a>
                <a
                  className="btn ghost"
                  href={result.pumpUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on pump.fun
                </a>
              </p>
              {feeShareNote && (
                <p className="field-note" style={{ marginTop: '0.75rem' }}>
                  {feeShareNote}
                </p>
              )}
              <p className="fineprint">
                The pump.fun link is a post-create viewer only — create already
                happened on this site.
              </p>
            </div>
          )}

          <details className="operator-debug">
            <summary>Operators only — debug / fallback</summary>
            <p className="muted">
              Not the user path. On-site Launch via the SDK is the product.
            </p>
            <CopyField
              label="Metadata URI"
              value={uploadedUri || TOKEN.metadataUri}
              note={
                uploadedUri
                  ? 'Uploaded for this launch (IPFS).'
                  : 'Static fallback until Launch uploads.'
              }
            />
            <CopyField
              label="Image URI"
              value={uploadedImageUri || TOKEN.imageUri}
            />
            <p>
              <a href={PUMP_CREATE_URL} target="_blank" rel="noreferrer">
                Open pump.fun/create
              </a>{' '}
              (manual fallback — do not treat as the launch CTA)
            </p>
            <div className="wallet-row" style={{ marginTop: '0.75rem' }}>
              <WalletMultiButton />
            </div>
          </details>
        </div>

        <aside className="create-preview-col">
          <div className="preview-card">
            <h3>Your token</h3>
            <div className="preview-media">
              {logoPreview || TOKEN.logoPath ? (
                <img src={logoPreview || TOKEN.logoPath} alt="" />
              ) : (
                <span className="placeholder-icon">◇</span>
              )}
            </div>
            <div className="preview-meta">
              <span className="pname">{displayName}</span>
              <span className="pticker">${displaySymbol}</span>
              <p className="pdesc">
                {form.description.trim() || 'Description appears here as you type.'}
              </p>
            </div>
            <div className="preview-fee">
              <span>Network</span>
              <strong>Solana</strong>
            </div>
            <div className="preview-fee" style={{ marginTop: 0, borderTop: 'none', paddingTop: 0 }}>
              <span>Create path</span>
              <strong>on-site SDK</strong>
            </div>
          </div>
          <p className="fineprint">
            Metadata (name, symbol, description, website, socials, image)
            uploads via <code>/ipfs-upload</code> before your wallet signs
            create.
          </p>
        </aside>

        <div className="create-cta-bar">
          <p className="fee-line">
            <span aria-hidden="true">◎</span>
            <span>
              Solana mainnet · small SOL for rent + fees
              {devBuy.trim() ? ` · optional buy UI: ${devBuy.trim()} SOL (not sent)` : ''}
            </span>
          </p>
          {!wallet.connected ? (
            <>
              {/*
                WalletMultiButton is the connect control (opens WalletModal via
                useWalletModal). Styled as the full-width lime CTA. Header also
                has WalletMultiButton. openWalletModal remains available if we
                need a plain button fallback.
              */}
              <div className="lime-wallet-cta-wrap">
                <WalletMultiButton>Connect wallet</WalletMultiButton>
              </div>
              {walletError && (
                <p className="wallet-hint" role="alert">
                  {walletError}{' '}
                  <button type="button" className="linkish" onClick={() => void retryWalletConnect()}>
                    Retry connect
                  </button>
                </p>
              )}
              {showInstallHint && !walletError && (
                <p className="wallet-hint" role="status">
                  Install{' '}
                  <a href="https://phantom.app/" target="_blank" rel="noreferrer">
                    Phantom
                  </a>{' '}
                  or{' '}
                  <a href="https://solflare.com/" target="_blank" rel="noreferrer">
                    Solflare
                  </a>
                  , then refresh this page.
                </p>
              )}
            </>
          ) : (
            <button
              type="button"
              className="btn lime-cta"
              disabled={busy || !ENABLE_SDK_CREATE}
              onClick={onPrimaryCta}
            >
              {ctaLabel}
            </button>
          )}
          <p className="fineprint">
            Your wallet signs; no private keys leave the browser. Never sent to
            pump.fun/create as the primary path.
          </p>
        </div>
      </div>
    </div>
  )
}
